'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { failureFromCode } from '@/lib/knowledge/ask-errors'
import { applyAskEvent, endTurn, failTurn, finishedTurn, startTurn, type AskTurn } from '@/lib/knowledge/ask-turn'
import { AskRefusal } from '@/lib/knowledge/portal-api'
import type { AskEvent, ConversationDetail } from '@/lib/knowledge/portal-types'

/** One line of the thread: the person's question, or an answer. */
export type ThreadEntry = { kind: 'question'; id: string; text: string } | { kind: 'answer'; id: string; turn: AskTurn }

export interface AskStart {
  question: string
  /** The conversation the last stream named; null for a new one (and always on the admin preview). */
  conversationId: string | null
  signal: AbortSignal
  onEvent: (event: AskEvent) => void
}

/** The thread a stored conversation reads as. */
export function entriesFromConversation(detail: ConversationDetail): ThreadEntry[] {
  return detail.messages.map((m): ThreadEntry =>
    m.role === 'user'
      ? { kind: 'question', id: `q-${m.id}`, text: m.content }
      : { kind: 'answer', id: `a-${m.id}`, turn: finishedTurn(`a-${m.id}`, m.outcome, m.content, m.citations, m.id) },
  )
}

/**
 * A conversation with the assistant: the questions asked, their answers as they
 * stream, and the conversation id the stream named (sent back with the next
 * question). One question at a time; starting over aborts the stream in flight.
 */
export function useAnswerSession(start: (args: AskStart) => Promise<void>, onDone?: (event: Extract<AskEvent, { type: 'done' }>) => void) {
  const [entries, setEntries] = useState<ThreadEntry[]>([])
  const [busy, setBusy] = useState(false)
  const [conversationId, setConversationId] = useState<string | null>(null)
  // The conversation's title as the API named it (the stream's `conversation` event, or a stored one).
  const [title, setTitle] = useState<string | null>(null)
  const conversationRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const seq = useRef(0)
  const startRef = useRef(start)
  const onDoneRef = useRef(onDone)
  useEffect(() => {
    startRef.current = start
    onDoneRef.current = onDone
  })

  const setConversation = useCallback((id: string | null) => {
    conversationRef.current = id
    setConversationId(id)
  }, [])

  const ask = useCallback(
    async (question: string) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      seq.current += 1
      const answerId = `a-live-${seq.current}`
      setEntries((prev) => [
        ...prev,
        { kind: 'question', id: `q-live-${seq.current}`, text: question },
        { kind: 'answer', id: answerId, turn: startTurn(answerId) },
      ])
      setBusy(true)
      const update = (fn: (turn: AskTurn) => AskTurn) =>
        setEntries((prev) => prev.map((e) => (e.kind === 'answer' && e.id === answerId ? { ...e, turn: fn(e.turn) } : e)))

      try {
        await startRef.current({
          question,
          conversationId: conversationRef.current,
          signal: controller.signal,
          onEvent: (event) => {
            if (controller.signal.aborted) return
            if (event.type === 'conversation') {
              setConversation(event.id)
              if (event.title) setTitle(event.title)
            }
            if (event.type === 'error' && failureFromCode(event.errorCode) === 'conversationNotFound') {
              setConversation(null)
              setTitle(null)
            }
            if (event.type === 'done') onDoneRef.current?.(event)
            update((turn) => applyAskEvent(turn, event))
          },
        })
        if (!controller.signal.aborted) update(endTurn)
      } catch (err) {
        if (controller.signal.aborted) return
        const failure = err instanceof AskRefusal ? err.failure : 'generic'
        if (failure === 'conversationNotFound') {
          setConversation(null)
          setTitle(null)
        }
        update((turn) => failTurn(turn, failure))
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null
          setBusy(false)
        }
      }
    },
    [setConversation],
  )

  const reset = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    setConversation(null)
    setTitle(null)
    setEntries([])
    setBusy(false)
  }, [setConversation])

  const load = useCallback(
    (detail: ConversationDetail) => {
      abortRef.current?.abort()
      abortRef.current = null
      setConversation(detail.id)
      setTitle(detail.title)
      setEntries(entriesFromConversation(detail))
      setBusy(false)
    },
    [setConversation],
  )

  useEffect(() => () => abortRef.current?.abort(), [])

  const last = entries[entries.length - 1]
  const lastFailure = last && last.kind === 'answer' ? last.turn.failure : null

  return { entries, busy, conversationId, title, lastFailure, ask, reset, load }
}

/** One conversation with the assistant, as `useAnswerSession` gives it. */
export type AnswerSession = ReturnType<typeof useAnswerSession>
